import { ChevronRight, Mail, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { Reveal } from '../hooks/useReveal.tsx';
import { useMouseParallax, useFloat } from '../hooks/useParallax.tsx';
import { MeshText } from './MeshText';

// Floating geometric shapes for visual depth
function FloatingShapes() {
  const shapes = [
    { size: 80, x: '10%', y: '20%', delay: 0, color: 'rgba(97, 218, 251, 0.15)' }, // React blue
    { size: 60, x: '85%', y: '15%', delay: 0.5, color: 'rgba(49, 120, 198, 0.12)' }, // TS blue
    { size: 100, x: '75%', y: '70%', delay: 1, color: 'rgba(247, 223, 30, 0.08)' }, // JS yellow
    { size: 40, x: '5%', y: '75%', delay: 1.5, color: 'rgba(55, 118, 171, 0.1)' }, // Python blue
    { size: 50, x: '50%', y: '10%', delay: 2, color: 'rgba(255, 255, 255, 0.05)' },
    { size: 70, x: '90%', y: '45%', delay: 2.5, color: 'rgba(97, 218, 251, 0.08)' },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map((shape, index) => (
        <FloatingShape key={index} {...shape} index={index} />
      ))}
    </div>
  );
}

function FloatingShape({ 
  size, 
  x, 
  y, 
  delay, 
  color, 
  index 
}: { 
  size: number; 
  x: string; 
  y: string; 
  delay: number; 
  color: string;
  index: number;
}) {
  const { floatY, mouseX, mouseY } = useFloat(15, 4 + index * 0.5, delay);
  
  // Different shapes respond differently to mouse
  const mouseSensitivity = 0.3 + (index % 3) * 0.2;
  
  return (
    <motion.div
      className="absolute rounded-full blur-sm"
      style={{
        width: size,
        height: size,
        left: x,
        top: y,
        backgroundColor: color,
        x: mouseX ? mouseX.get() * mouseSensitivity * (index % 2 === 0 ? 1 : -1) : 0,
        y: floatY + (mouseY ? mouseY.get() * mouseSensitivity * (index % 2 === 0 ? -1 : 1) : 0),
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 1.5, 
        delay: delay * 0.3,
        ease: 'easeOut'
      }}
    />
  );
}

// Parallax text component
function ParallaxText({ children, className, depth = 1 }: { 
  children: React.ReactNode; 
  className?: string;
  depth?: number;
}) {
  const { x, y } = useMouseParallax(0.015 * depth);
  
  return (
    <motion.div 
      className={className}
      style={{ x, y }}
    >
      {children}
    </motion.div>
  );
}

export function HeroSection() {
  const services = [
    '/ FULL STACK DEVELOPMENT',
    '/ AI & MACHINE LEARNING',
    '/ WEB APPLICATIONS',
  ];

  const { x: mouseX, y: mouseY } = useMouseParallax(0.02);

  return (
    <section
      id="hero"
      className="relative min-h-screen supports-[height:100svh]:min-h-[100svh] flex flex-col justify-between px-5 sm:px-8 md:px-12 pt-24 sm:pt-28 pb-12 md:pb-16"
    >
      {/* Floating geometric shapes */}
      <FloatingShapes />

      {/* Top Row */}
      <div className="relative z-10 flex flex-col gap-8 sm:flex-row sm:justify-between">
        {/* Service List */}
        <div className="flex flex-col gap-2">
          {services.map((service, index) => (
            <Reveal key={service} delay={150 + index * 120}>
              <ParallaxText depth={0.5 + index * 0.2}>
                <div className="h-[16px] w-[280px]">
                  <MeshText
                    text={service}
                    color="rgba(255,255,255,0.9)"
                    fontFamily="monospace"
                    fontWeight={400}
                    fontSize={12}
                    colorSplit={true}
                    splitColors={['#61dafb', '#ff40c0']}
                    force={10}
                  />
                </div>
              </ParallaxText>
            </Reveal>
          ))}
        </div>

        {/* Intro Text */}
        <Reveal delay={300} className="max-w-xs sm:text-right">
          <ParallaxText depth={0.8}>
            <p className="text-lg sm:text-xl leading-relaxed text-white text-shadow">
              Building digital experiences with clarity, precision, and modern technologies that make a difference.
            </p>
          </ParallaxText>
        </Reveal>
      </div>

      {/* Bottom Row */}
      <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between mt-auto">
        {/* Left - Name and Badge */}
        <div>
          {/* Badge */}
          <Reveal delay={150}>
            <ParallaxText depth={0.6}>
              <div className="accent-badge px-3 py-1.5 rounded-r-md inline-block mb-5">
                <span className="mono-label-sm text-white/90">
                  Software Engineering @ DTU
                </span>
              </div>
            </ParallaxText>
          </Reveal>

          {/* Main Headline with mesh text effect */}
          <Reveal delay={280}>
            <div className="flex flex-col gap-0">
              <div className="h-[70px] sm:h-[90px] lg:h-[110px] w-[320px] sm:w-[420px] lg:w-[520px]">
                <MeshText
                  text="Code. Create."
                  color="#ffffff"
                  fontFamily="Inter"
                  fontWeight={600}
                  fontSize={70}
                  colorSplit={true}
                  splitColors={['#61dafb', '#ff40c0']}
                  force={20}
                />
              </div>
              <div className="h-[70px] sm:h-[90px] lg:h-[110px] w-[280px] sm:w-[350px] lg:w-[420px]">
                <MeshText
                  text="Innovate."
                  color="#ffffff"
                  fontFamily="Inter"
                  fontWeight={600}
                  fontSize={70}
                  colorSplit={true}
                  splitColors={['#40ff80', '#61dafb']}
                  force={20}
                />
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right - Contact Card with subtle tilt effect */}
        <Reveal delay={420}>
          <motion.div 
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-xl bg-white/15 p-4 sm:p-5 backdrop-blur-md border border-white/15 max-w-md"
            style={{ 
              x: mouseX ? mouseX.get() * -0.5 : 0,
              y: mouseY ? mouseY.get() * -0.5 : 0,
            }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
          >
            {/* Avatar placeholder with initials */}
            <motion.div 
              className="h-20 w-16 sm:h-24 sm:w-20 rounded-lg bg-gradient-to-br from-white/20 to-white/5 flex items-center justify-center flex-shrink-0"
              whileHover={{ 
                rotateY: 10,
                transition: { duration: 0.3 }
              }}
            >
              <span className="text-2xl sm:text-3xl font-semibold text-white/80">P</span>
            </motion.div>

            {/* Text and Button */}
            <div className="flex flex-col gap-2 pr-2">
              <div>
                <div className="h-[18px] w-[220px]">
                  <MeshText
                    text="Let's build something amazing"
                    color="#ffffff"
                    fontFamily="Inter"
                    fontWeight={500}
                    fontSize={14}
                    colorSplit={true}
                    splitColors={['#61dafb', '#40ff80']}
                    force={10}
                  />
                </div>
                <div className="h-[14px] w-[120px] mt-0.5">
                  <MeshText
                    text="Paras Kanojia"
                    color="rgba(255,255,255,0.6)"
                    fontFamily="monospace"
                    fontWeight={400}
                    fontSize={11}
                    colorSplit={true}
                    splitColors={['#ff40c0', '#61dafb']}
                    force={8}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-1.5">
                <motion.a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Mail size={14} />
                  Get in Touch
                  <ChevronRight size={14} />
                </motion.a>
                <motion.a
                  href="/Resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FileText size={14} />
                  Resume
                </motion.a>
              </div>
            </div>
          </motion.div>
        </Reveal>
      </div>

      {/* Scroll indicator with parallax */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        style={{
          y: mouseY ? mouseY.get() * -0.3 : 0,
        }}
      >
        <span className="mono-label text-white/40 text-xs">Scroll to explore</span>
        <motion.div
          className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center p-1"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <motion.div 
            className="w-1 h-2 rounded-full bg-white/40"
            animate={{ y: [0, 8, 0], opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
